import { buildCreateAssetPayload } from '@/features/assets/api/create-asset/create-asset.payload';
import { apiClient } from '@borradh-workspace/api-client';
import { useMutation } from '@tanstack/react-query';
import { validateAdCreativeFile } from '../media-upload-validation';

// Keep the completed object when saving its library record fails. Weak keys
// retain nothing once the dialog releases the File; retries reuse that object.
const completedUploads = new WeakMap<File, string>();

/** Save replacement/reference media in the owned library, without editing an ad. */
export async function uploadAdCreative(file: File): Promise<string> {
  const error = validateAdCreativeFile(file);
  if (error) throw new Error(error);
  const type = file.type.startsWith('video/') ? 'video' : 'image';
  const { uploadFileToS3 } = await import(
    '@/features/upload/api/resumable-upload'
  );
  let blobUrl = completedUploads.get(file);
  if (!blobUrl) {
    const result = await uploadFileToS3(file, type, 'org-asset', {
      resumeNamespace: 'assistant-ad-creative',
    });
    blobUrl = result.url;
    completedUploads.set(file, blobUrl);
  }
  const asset = await apiClient.post<{ id: string }>(
    'assets',
    buildCreateAssetPayload({ file, blobUrl })
  );
  if (type === 'video') {
    // Match chat clip uploads: analysis enriches later generation/search, but
    // failure must not turn a successfully saved upload into a duplicate retry.
    void apiClient.post(`assets/${asset.id}/analyze`, {}).catch(() => {});
  }
  return asset.id;
}

export function useAdCreativeUpload() {
  const mutation = useMutation({ mutationFn: uploadAdCreative });
  return { upload: mutation.mutateAsync };
}
